import { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { Building2, RadioTower, Smartphone } from 'lucide-react';
import styles from './DeviceSelect.module.scss';

const normalize = value => (value ? String(value).trim() : '');

// Plain-text label: react-select filters on this, so it must contain every
// piece of information the operator may search by.
const searchText = device =>
  [
    device.device_id,
    device.nickname,
    device.hub_serial_number,
    device.machine_nickname,
    device.company_name,
  ]
    .map(normalize)
    .filter(Boolean)
    .join(' ');

const buildOption = device => ({
  value: device.device_id,
  label: searchText(device),
  device,
});

export function DeviceOptionLabel({ device, compact = false, badge = null }) {
  const hasHub = Boolean(device.hub_serial_number);
  return (
    <div className={`${styles.option} ${compact ? styles.compact : ''}`}>
      <span className={styles.headline}>
        <Smartphone size={13} aria-hidden="true" />
        <strong>{device.nickname || 'Unnamed device'}</strong>
        <code>{device.device_id}</code>
        {badge && <b>{badge}</b>}
      </span>
      {!compact && (
        <span className={styles.subline}>
          <small className={styles.hub}>
            <RadioTower size={12} aria-hidden="true" />
            {hasHub
              ? `${device.machine_nickname || 'Unnamed machine'} · ${device.hub_serial_number}`
              : 'No hub linked'}
          </small>
          {device.company_name && (
            <small className={styles.company}>
              <Building2 size={12} aria-hidden="true" />
              {device.company_name}
            </small>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Searchable device picker. Lists every device the OTA service knows about
 * (searchable by device ID, nickname, hub serial, machine name or company)
 * and still accepts an arbitrary device ID typed by hand.
 */
function DeviceSelect({
  inputId,
  devices,
  value,
  onChange,
  placeholder = 'Search device, nickname, machine or company…',
  isDisabled = false,
  badgeFor = () => null,
}) {
  const options = useMemo(() => devices.map(buildOption), [devices]);
  const selectedValue = normalize(value);

  const { selectedOption, allOptions } = useMemo(() => {
    const match = options.find(option => option.value === selectedValue) || null;
    if (match || !selectedValue) {
      return { selectedOption: match, allOptions: options };
    }
    // Hand-typed ID that is not in the list yet: keep it selectable.
    const custom = buildOption({ device_id: selectedValue, nickname: null, manual: true });
    return { selectedOption: custom, allOptions: [custom, ...options] };
  }, [options, selectedValue]);

  return (
    <CreatableSelect
      inputId={inputId}
      classNamePrefix="deviceSelect"
      options={allOptions}
      value={selectedOption}
      onChange={option => onChange(option ? option.value : '', option?.device || null)}
      onCreateOption={inputValue => {
        const deviceId = normalize(inputValue);
        if (deviceId) {
          onChange(deviceId, null);
        }
      }}
      formatOptionLabel={(option, { context }) => {
        if (option.__isNew__) {
          return (
            <div className={styles.createOption}>
              Use device ID <code>{normalize(option.value)}</code>
            </div>
          );
        }
        return (
          <DeviceOptionLabel
            device={option.device}
            compact={context === 'value'}
            badge={option.device.manual ? 'new' : badgeFor(option.device)}
          />
        );
      }}
      formatCreateLabel={inputValue => `Use device ID "${normalize(inputValue)}"`}
      isValidNewOption={inputValue => {
        const candidate = normalize(inputValue);
        return Boolean(candidate) && !allOptions.some(option => option.value === candidate);
      }}
      isClearable
      isSearchable
      isDisabled={isDisabled}
      placeholder={placeholder}
      noOptionsMessage={() => 'No matching device'}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuPlacement="auto"
      maxMenuHeight={360}
    />
  );
}

export default DeviceSelect;
